import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import SellIcon from '@mui/icons-material/Sell';
import {
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import type { OpportunityRelatedExtraGuestSurcharge } from '~/hooks/apis/offers/useGetOpportunityRelated';

import type { SalesforceSurchargePeriod } from '~/types/surcharges';

import { formatDateRange } from '~/utils/dateRangeUtils';

import { DEFAULT_CURRENCY, formatDealOptionCurrency, formatDealOptionPercent } from './dealOptionsFormatters';
import {
  type RoomInfoTab,
  type SelectedRoomInfo,
  getDisplayBlackoutEntry,
  getMarginCheckColor,
} from './dealOptionsRoomInfo';

interface Props {
  activeTab: RoomInfoTab;
  egsRows: Array<OpportunityRelatedExtraGuestSurcharge>;
  onActiveTabChange: (tab: RoomInfoTab) => void;
  onClose: () => void;
  salesforceSurcharges: Array<SalesforceSurchargePeriod>;
  selectedRoomInfo: SelectedRoomInfo | null;
}

function getEgsTypeChipSx(type: string | null | undefined) {
  const normalizedType = type?.toLowerCase();

  if (normalizedType?.includes('adult') || normalizedType?.includes('child')) {
    return {
      bgcolor: (theme) => alpha(theme.palette.info.main, 0.12),
      borderColor: (theme) => alpha(theme.palette.info.main, 0.28),
      color: 'info.main',
    };
  }

  if (normalizedType?.includes('infant')) {
    return {
      bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.12),
      borderColor: (theme) => alpha(theme.palette.secondary.main, 0.28),
      color: 'secondary.main',
    };
  }

  return {
    bgcolor: (theme) => alpha(theme.palette.text.secondary, 0.08),
    borderColor: (theme) => alpha(theme.palette.text.secondary, 0.24),
    color: 'text.secondary',
  };
}

function renderEgsRows(egsRows: Array<OpportunityRelatedExtraGuestSurcharge>) {
  if (egsRows.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 2 }} variant="body2">
        No extra guest surcharges.
      </Typography>
    );
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow
          sx={{
            '& .MuiTableCell-head': {
              bgcolor: 'grey.50',
              color: 'text.secondary',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0,
              textTransform: 'uppercase',
            },
          }}
        >
          <TableCell sx={{ width: 56 }}>#</TableCell>
          <TableCell>Type</TableCell>
          <TableCell>Age Range</TableCell>
          <TableCell>Collected By</TableCell>
          <TableCell>Bedding</TableCell>
          <TableCell align="right">Cost</TableCell>
          <TableCell align="right">Sell Price</TableCell>
          <TableCell align="right">Margin</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {egsRows.map((egsRow, index) => (
          <TableRow key={egsRow.id}>
            <TableCell>{index + 1}</TableCell>
            <TableCell>
              <Chip
                label={egsRow.extra_guest_type__c || '—'}
                size="small"
                sx={getEgsTypeChipSx(egsRow.extra_guest_type__c)}
                variant="outlined"
              />
            </TableCell>
            <TableCell>{egsRow.age_range__c || '—'}</TableCell>
            <TableCell>{egsRow.collected_by__c || '—'}</TableCell>
            <TableCell>{egsRow.other_extra_bedding_type__c || egsRow.bedding_type__c || '—'}</TableCell>
            <TableCell align="right">
              <Stack alignItems="flex-end" spacing={0.25}>
                <Typography fontWeight={700} variant="body2">
                  {formatDealOptionCurrency(egsRow.currency, egsRow.cost_price__c)}
                </Typography>
                {egsRow.converted_cost__c && (
                  <Typography color="text.secondary" variant="caption">
                    ({formatDealOptionCurrency(DEFAULT_CURRENCY, egsRow.converted_cost__c)})
                  </Typography>
                )}
              </Stack>
            </TableCell>
            <TableCell align="right">{formatDealOptionCurrency(egsRow.currency, egsRow.sell_price_aud__c)}</TableCell>
            <TableCell align="right">
              <Typography
                color={getMarginCheckColor(egsRow.extra_night_margin_check__c)}
                fontWeight={700}
                variant="body2"
              >
                {formatDealOptionPercent(egsRow.margin__c)}
              </Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function DealOptionRoomInfoDialog({
  activeTab,
  egsRows,
  onActiveTabChange,
  onClose,
  salesforceSurcharges,
  selectedRoomInfo,
}: Props) {
  return (
    <Dialog fullWidth maxWidth="lg" open={!!selectedRoomInfo} onClose={onClose}>
      <DialogTitle sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', pr: 1 }}>
        <Typography fontWeight={700}>{selectedRoomInfo?.group.roomName}</Typography>
        <IconButton aria-label="Close room information" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Tabs value={activeTab} onChange={(_event, nextTab: RoomInfoTab) => onActiveTabChange(nextTab)}>
          <Tab
            icon={<GroupAddIcon fontSize="small" />}
            iconPosition="start"
            label={
              <Stack alignItems="center" direction="row" spacing={1}>
                <span>Extra Guest Surcharges</span>
                <Chip color="primary" label={egsRows.length} size="small" sx={{ height: 20 }} />
              </Stack>
            }
            value="egs"
          />
          <Tab
            icon={<SellIcon fontSize="small" />}
            iconPosition="start"
            label={
              <Stack alignItems="center" direction="row" spacing={1}>
                <span>Surcharges</span>
                <Chip label={salesforceSurcharges.length} size="small" sx={{ height: 20 }} />
              </Stack>
            }
            value="surcharges"
          />
          <Tab
            icon={<EventBusyIcon fontSize="small" />}
            iconPosition="start"
            label={
              <Stack alignItems="center" direction="row" spacing={1}>
                <span>Blackout Dates</span>
                <Chip label={selectedRoomInfo?.blackouts.length ?? 0} size="small" sx={{ height: 20 }} />
              </Stack>
            }
            value="blackouts"
          />
        </Tabs>
        <Stack sx={{ pt: 2 }}>
          {activeTab === 'egs' && renderEgsRows(egsRows)}
          {activeTab === 'surcharges' &&
            (salesforceSurcharges.length ? (
              salesforceSurcharges.map((surcharge) => (
                <Stack
                  key={surcharge.id}
                  alignItems="center"
                  direction="row"
                  justifyContent="space-between"
                  spacing={2}
                  sx={{ borderBottom: 1, borderColor: 'divider', py: 1 }}
                >
                  <Stack>
                    <Typography fontWeight={500} variant="body2">
                      {surcharge.name}
                    </Typography>
                    <Typography color="text.secondary" variant="caption">
                      {formatDateRange(surcharge.start_date, surcharge.end_date)}
                    </Typography>
                  </Stack>
                  <Typography sx={{ whiteSpace: 'nowrap' }} variant="body2">
                    {formatDealOptionCurrency(surcharge.currency_code, surcharge.cost_price)} cost ·{' '}
                    <strong>{formatDealOptionCurrency(surcharge.currency_code, surcharge.sell_price)}</strong> sell
                  </Typography>
                </Stack>
              ))
            ) : (
              <Typography color="text.secondary" sx={{ py: 2 }} variant="body2">
                No surcharges.
              </Typography>
            ))}
          {activeTab === 'blackouts' &&
            (selectedRoomInfo?.blackouts.length ? (
              selectedRoomInfo.blackouts.map((blackout) => {
                const displayBlackout = getDisplayBlackoutEntry(blackout);

                return (
                  <Stack
                    key={blackout.id}
                    alignItems="center"
                    direction="row"
                    justifyContent="space-between"
                    spacing={2}
                    sx={{ borderBottom: 1, borderColor: 'divider', py: 1 }}
                  >
                    <Typography variant="body2">{displayBlackout.name}</Typography>
                    {displayBlackout.dateRange && (
                      <Typography color="text.secondary" sx={{ whiteSpace: 'nowrap' }} variant="body2">
                        {displayBlackout.dateRange}
                      </Typography>
                    )}
                  </Stack>
                );
              })
            ) : (
              <Typography color="text.secondary" sx={{ py: 2 }} variant="body2">
                No blackout dates.
              </Typography>
            ))}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
